import { Delete, Edit, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { t } from 'i18next'
import React, { useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import { useNotification } from '../../store/redux/hooks'
import { useDeleteContractMutation } from '../../store/redux/reducers/contractSlice'
import { ContractLightDTO } from '../../store/types/models/Contract'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'

type MenuPopupProps = {
  contract: ContractLightDTO
  onDeleted?: (id: string) => void
}

const EmploymentMenuPopup: React.FC<MenuPopupProps> = ({ contract, onDeleted }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { displayNotification } = useNotification()
  const [deleteContract, { isLoading: isContractDeleteLoading }] = useDeleteContractMutation()
  const navigate = useNavigate()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteContract({ id: contract.id }).unwrap()
      displayNotification({
        message: t('page.employments.dialog.employmentRemoved'),
        type: 'success',
      })
      if (onDeleted) onDeleted(contract.id)
    } catch (_) {
      displayNotification({
        message: t('page.employments.dialog.employmentRemovedError'),
        type: 'error',
      })
    }
    setOpenDeleteDialog(false)
  }

  return (
    <>
      <IconButton onClick={handleClick} sx={{ svg: { color: 'text.mediumEmphasis' } }} aria-label="Open action menu">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box>
          <MenuItem
            onClick={() => {
              handleClose()
              navigate(generatePath(navigation.editContract, { userId: contract.employeeId, contractId: contract.id }))
            }}
            sx={{ color: 'text.highEmphasis', svg: { color: 'text.highEmphasis' } }}
          >
            <ListItemIcon>
              <Edit />
            </ListItemIcon>
            <ListItemText>{t('page.employments.actionPopup.edit')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setOpenDeleteDialog(true)} sx={{ color: 'warning.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Delete />
            </ListItemIcon>
            <ListItemText>{t('page.employments.actionPopup.delete')}</ListItemText>
          </MenuItem>
        </Box>
      </Menu>
      <ConfirmationDialog
        open={openDeleteDialog}
        loading={isContractDeleteLoading}
        setOpen={setOpenDeleteDialog}
        title={t('page.employments.dialog.delete.title')}
        content={
          <Stack direction={'column'}>
            <Typography variant="body1" fontWeight={500}>
              {contract.contractTypeValue}
            </Typography>
            <Typography variant="body1" color="text.dark">
              {contract.startDate} - {contract.endDate}
            </Typography>
          </Stack>
        }
        handleConfirm={handleConfirmDelete}
      />
    </>
  )
}

export default EmploymentMenuPopup
